import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCartRedux from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import Modal from "@insite/mobius/Modal";
import {
    CartProvider,
    logger,
    StorefrontModel,
    TranslationId,
    useFetchCurrentCart,
    useFormattedMessage,
    useInvalidateCurrentCart,
} from "@sonic-equipment/ui";
import { getCurrency } from "@sonic/Handlers/Tracking/AddToCartTracking";
import React, { ReactElement, useEffect } from "react";
import { useRef } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const settingsCollection = getSettingsCollection(state);
    const currentCart = getCurrentCartState(state);
    return {
        showAddToCartConfirmationDialog: settingsCollection.productSettings.showAddToCartConfirmationDialog,
        addToCartPopupTimeout: settingsCollection.cartSettings.addToCartPopupTimeout,
        currentCart,
        billTo: getBillToState(state, currentCart.value?.billToId),
        currency: getCurrency(currentCart.value?.currencySymbol),
    };
};

const mapDispatchToProps = {
    loadCurrentCart: loadCurrentCartRedux,
};

type Props = ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export const ReduxCartProvider = connect(
    mapStateToProps,
    mapDispatchToProps,
)((props: Props): ReactElement | null => {
    const [errorModalIsOpen, setErrorModalIsOpen] = React.useState(false);
    const invalidateCurrentCart = useInvalidateCurrentCart();
    const { data: cart } = useFetchCurrentCart();
    const t = useFormattedMessage();
    const closeErrorModal = () => {
        setErrorModalIsOpen(false);
    };
    const changedCartLine = useRef<StorefrontModel.CartLineModel | null>(null);

    useEffect(() => {
        // Everytime the redux cart changes check if the Redux cart state is different
        // then the cached react query cart state and invalidate the cached cart state
        // when needed
        if (props.currentCart.isLoading) {
            return;
        }
        if (JSON.stringify(props.currentCart) === JSON.stringify(cart)) {
            return;
        }
        invalidateCurrentCart();
    }, [props.currentCart]);

    return (
        <>
            <Modal
                isOpen={errorModalIsOpen}
                closeOnEsc={true}
                handleClose={closeErrorModal}
                headline={t("An Error Occurred." as TranslationId)}
            >
                {t(
                    "An error occurred so we didn't receive your message. Please try again or use the Contact Us form instead." as TranslationId,
                )}
            </Modal>
            <CartProvider
                onCartLineError={error => {
                    logger.error("error", error);
                    setErrorModalIsOpen(true);
                }}
                onCartLineAdded={cartLine => {
                    props.loadCurrentCart();
                    changedCartLine.current = cartLine as StorefrontModel.CartLineModel;
                }}
                onCartLineRemoved={() => {
                    props.loadCurrentCart();
                }}
                onCartLineUpdated={() => {
                    props.loadCurrentCart();
                }}
            />
        </>
    );
});
